import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AgentMcpLinkService, type AgentMcpLink } from '../../../services/agent-mcp-link.service';

/**
 * "Connect Claude" — the private connector link for the agent export's MCP
 * server (AGENT_EXPORT_MCP.md §4.2). Shows the site's link with copy and
 * revoke; the link lets the client's own Claude read the review queue,
 * read-only.
 *
 * The link is a secret, so the panel is closed until asked for, and creating
 * one is its own click: opening the panel never mints a token. Revoking asks
 * once more inline rather than through `confirm()`, which would block the page.
 */
@Component({
  selector: 'app-connect-claude',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './connect-claude.html',
  styleUrl: './connect-claude.css'
})
export class ConnectClaudeComponent implements OnChanges {
  /** The domain selected in the header — a link reads this site only. */
  @Input() domain: string | null = null;

  open = false;
  /** undefined: not loaded yet; null: the site has no live link. */
  link: AgentMcpLink | null | undefined = undefined;
  busy = false;
  confirmingRevoke = false;
  notice: string | null = null;
  error: string | null = null;

  constructor(private links: AgentMcpLinkService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['domain'] && !changes['domain'].firstChange) {
      this.open = false;
      this.reset();
    }
  }

  private reset(): void {
    this.link = undefined;
    this.busy = false;
    this.confirmingRevoke = false;
    this.notice = null;
    this.error = null;
  }

  private get site(): string {
    return (this.domain || '').trim();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open && this.link === undefined) void this.load();
  }

  async load(): Promise<void> {
    if (!this.site) return;
    this.busy = true;
    this.error = null;
    try {
      this.link = await firstValueFrom(this.links.get(this.site));
    } catch {
      this.error = 'Could not check for a link. Close and reopen to try again.';
    } finally {
      this.busy = false;
    }
  }

  async create(): Promise<void> {
    if (!this.site || this.busy) return;
    this.busy = true;
    this.error = null;
    this.notice = null;
    try {
      this.link = await firstValueFrom(this.links.create(this.site));
    } catch {
      this.error = 'The link was not created. Try again in a minute.';
    } finally {
      this.busy = false;
    }
  }

  async copy(): Promise<void> {
    if (!this.link) return;
    this.error = null;
    try {
      await navigator.clipboard.writeText(this.link.connectorUrl);
      this.notice = 'Copied. Paste it into Claude as a custom connector.';
    } catch {
      this.notice = null;
      this.error = 'Your browser blocked copying. Select the link above and copy it by hand.';
    }
  }

  async revoke(): Promise<void> {
    if (!this.link || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      await firstValueFrom(this.links.revoke(this.link.token));
      this.link = null;
      this.confirmingRevoke = false;
      this.notice = 'Link revoked. Claude can no longer read this site through it.';
    } catch {
      // Not "try again" alone: the dangerous misreading is thinking it is dead.
      this.error = 'The link was not revoked and still works. Try again.';
    } finally {
      this.busy = false;
    }
  }
}
